import { useCallback, useMemo, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import classNames from 'classnames';

import Overlay from 'react-overlays/Overlay';
import type { Placement, State } from 'react-overlays/usePopper';

import ArticleIcon from '@/material-icons/400-24px/article.svg?react';
import CodeIcon from '@/material-icons/400-24px/code.svg?react';
import { changeComposeInputMode } from 'mastodon/actions/compose';
import type { SelectItem } from 'mastodon/components/dropdown_selector';
import { DropdownSelector } from 'mastodon/components/dropdown_selector';
import { Icon } from 'mastodon/components/icon';
import {
  COMPOSE_INPUT_MODE_MARKDOWN,
  COMPOSE_INPUT_MODE_PLAIN,
} from 'mastodon/features/compose/input_modes';
import type { ComposeInputMode } from 'mastodon/features/compose/input_modes';
import { useAppDispatch, useAppSelector } from 'mastodon/store';

const messages = defineMessages({
  changeInputMode: {
    id: 'compose.input_mode.change',
    defaultMessage: 'Change input mode',
  },
  plain: {
    id: 'compose.input_mode.plain',
    defaultMessage: 'Plain text',
  },
  plainDescription: {
    id: 'compose.input_mode.plain.description',
    defaultMessage: 'Write your post as plain text',
  },
  markdown: {
    id: 'compose.input_mode.markdown',
    defaultMessage: 'Markdown',
  },
  markdownDescription: {
    id: 'compose.input_mode.markdown.description',
    defaultMessage: 'Write your post with Markdown formatting',
  },
});

export const InputModeDropdown: React.FC = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<Placement | undefined>('bottom');
  const activeElementRef = useRef<HTMLElement | null>(null);
  const targetRef = useRef<HTMLDivElement>(null);

  const value = useAppSelector(
    (state) => state.compose.get('input_mode') as string,
  );

  const options = useMemo<SelectItem<ComposeInputMode>[]>(
    () => [
      {
        icon: 'article',
        iconComponent: ArticleIcon,
        value: COMPOSE_INPUT_MODE_PLAIN,
        text: intl.formatMessage(messages.plain),
        meta: intl.formatMessage(messages.plainDescription),
      },
      {
        icon: 'code',
        iconComponent: CodeIcon,
        value: COMPOSE_INPUT_MODE_MARKDOWN,
        text: intl.formatMessage(messages.markdown),
        meta: intl.formatMessage(messages.markdownDescription),
      },
    ],
    [intl],
  );

  const current =
    options.find((option) => option.value === value) ?? options[0];

  const handleMouseDown = useCallback(() => {
    if (!open && document.activeElement instanceof HTMLElement) {
      activeElementRef.current = document.activeElement;
    }
  }, [open]);

  const handleToggle = useCallback(() => {
    if (open && activeElementRef.current) {
      activeElementRef.current.focus({ preventScroll: true });
    }

    setOpen(!open);
  }, [open]);

  const handleClose = useCallback(() => {
    if (open && activeElementRef.current) {
      activeElementRef.current.focus({ preventScroll: true });
    }

    setOpen(false);
  }, [open]);

  const handleChange = useCallback(
    (inputMode: string) => {
      if (inputMode !== value) {
        dispatch(changeComposeInputMode(inputMode));
      }
    },
    [dispatch, value],
  );

  const handleOverlayEnter = useCallback((state: Partial<State>) => {
    setPlacement(state.placement);
  }, []);

  if (!current) {
    return null;
  }

  return (
    <div ref={targetRef}>
      <button
        type='button'
        title={intl.formatMessage(messages.changeInputMode)}
        aria-expanded={open}
        onClick={handleToggle}
        onMouseDown={handleMouseDown}
        className={classNames('dropdown-button', { active: open })}
      >
        {current.icon && current.iconComponent && (
          <Icon id={current.icon} icon={current.iconComponent} />
        )}
        <span className='dropdown-button__label'>{current.text}</span>
      </button>

      <Overlay
        show={open}
        offset={[5, 5]}
        placement={placement}
        flip
        target={targetRef}
        popperConfig={{ strategy: 'fixed', onFirstUpdate: handleOverlayEnter }}
      >
        {({ props, placement }) => (
          <div {...props}>
            <div
              className={`dropdown-animation privacy-dropdown__dropdown ${placement}`}
            >
              <DropdownSelector
                items={options}
                value={current.value}
                onClose={handleClose}
                onChange={handleChange}
              />
            </div>
          </div>
        )}
      </Overlay>
    </div>
  );
};
